import { Channel } from '@/models';
import { FilterState } from '@/types/filter';
import { SessionStorageKey, SESSION_STORAGE_SYNC_EVENT, clearSessionStorage } from '@/hooks/useSessionStorage';
import { isRecord } from '@/utils/filter/isRecord';

/**
 * Secondary segment (comparison) filters keyed by channel. Persisted to
 * sessionStorage so selections survive navigation and tab switches, and are
 * shared with the saved-filter / share-view machinery via LocalFiltersRegistry.
 */
export type SecondarySegmentFiltersState = Partial<Record<Channel, FilterState>>;

const STORAGE_KEY = SessionStorageKey.CHANNEL_FOCUS_SECONDARY_SEGMENT_FILTERS;

const isFilterState = (value: unknown): value is FilterState => isRecord(value);

const parseStored = (raw: string | null): SecondarySegmentFiltersState => {
  if (!raw) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed)) return {};
    const result: SecondarySegmentFiltersState = {};
    for (const channel of Object.values(Channel)) {
      const channelFilters = parsed[channel];
      if (isFilterState(channelFilters)) {
        result[channel] = channelFilters;
      }
    }
    return result;
  } catch {
    return {};
  }
};

export const readSecondarySegmentFilters = (): SecondarySegmentFiltersState =>
  parseStored(sessionStorage.getItem(STORAGE_KEY));

export const writeSecondarySegmentFilters = (state: SecondarySegmentFiltersState): void => {
  // source:'hook' marks the write as store-originated so the store's own
  // SESSION_STORAGE_SYNC_EVENT listener ignores it (preventing a feedback
  // loop), while non-hook listeners (e.g. the active filter bar) still react.
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  window.dispatchEvent(new CustomEvent(SESSION_STORAGE_SYNC_EVENT, { detail: { key: STORAGE_KEY, source: 'hook' } }));
};

export const clearSecondarySegmentFilters = (): void => {
  clearSessionStorage(STORAGE_KEY);
};

export const getSecondarySegmentFiltersForChannel = (channel: Channel): FilterState =>
  readSecondarySegmentFilters()[channel] ?? {};
